import React from 'react';
import { Loader2 } from 'lucide-react';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'icon';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--accent-primary)] text-[var(--text-inverse)] hover:brightness-110 active:brightness-95 font-medium font-mono uppercase',
  secondary:
    'bg-transparent border border-[var(--border-default)] text-[var(--text-primary)] font-mono uppercase ' +
    'hover:bg-[var(--bg-overlay)] hover:border-[var(--border-strong)]',
  danger:
    'bg-[var(--accent-danger-15)] border border-[var(--accent-danger-30)] text-[var(--accent-danger)] font-mono uppercase ' +
    'hover:bg-[var(--accent-danger-25)]',
  ghost:
    'bg-transparent text-[var(--text-secondary)] font-mono uppercase hover:bg-[var(--bg-overlay)] hover:text-[var(--text-primary)]',
  icon:
    'bg-transparent text-[var(--text-tertiary)] hover:bg-[var(--bg-overlay)] hover:text-[var(--text-secondary)] ' +
    'rounded-[2px] w-9 h-9 flex items-center justify-center p-0',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-[12px] rounded-[var(--radius-sm)]',
  md: 'px-5 py-[10px] text-[var(--text-body)] rounded-[var(--radius)]',
  lg: 'px-6 py-3 text-[15px] rounded-[var(--radius)]',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'secondary',
      size = 'md',
      loading = false,
      icon,
      children,
      className = '',
      disabled,
      ...rest
    },
    ref
  ) => {
    const isIcon = variant === 'icon';

    // Bracket framing for non-icon buttons gives the Shield AI / Anduril CTA look
    const wrapInBrackets = !isIcon && children;

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        style={{ letterSpacing: '0.08em', fontSize: '11px' }}
        className={[
          'inline-flex items-center justify-center gap-2 cursor-pointer',
          'transition-all duration-[150ms] ease-out',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent-primary)] focus-visible:ring-offset-0',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          variantStyles[variant],
          !isIcon ? sizeStyles[size] : '',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...rest}
      >
        {loading ? (
          <Loader2 size={14} className="animate-spin" />
        ) : icon ? (
          <span className="flex items-center">{icon}</span>
        ) : null}
        {wrapInBrackets ? (
          <span className="flex items-center gap-1.5">
            <span className="text-[var(--text-tertiary)] opacity-70">[</span>
            {children}
            <span className="text-[var(--text-tertiary)] opacity-70">]</span>
          </span>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
